import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useHostnameTenant } from '../context/HostnameTenantContext';

/**
 * Returns the effective tenant slug for the current request.
 *
 * Resolution order:
 * 1. When the hostname matched a registered tenant (status === 'matched'),
 *    returns the slug from the hostname resolution — no URL prefix is present.
 * 2. Otherwise falls back to the :tenantSlug route param from useParams().
 *
 * Use this hook as the single source of truth instead of calling
 * useParams().tenantSlug directly in components.
 *
 * @returns {string} The resolved tenant slug.
 */
export const useResolvedTenantSlug = () => {
  const { status, tenant } = useHostnameTenant();
  const params = useParams();

  if (status === 'matched' && tenant?.slug) {
    return tenant.slug;
  }

  return params.tenantSlug || '';
};

/**
 * Builds a tenant-aware path for use in Link / navigate calls.
 *
 * In matched-hostname mode (clean URLs) the slug prefix is omitted,
 * so `buildTenantPath('mvg', '/dashboard', true)` returns `/dashboard`.
 * In slug-prefixed mode it returns `/${slug}${suffix}`.
 *
 * @param {string} slug      - The tenant slug.
 * @param {string} suffix    - The path suffix, must start with '/'.
 * @param {boolean} matched  - Whether the hostname was matched.
 * @returns {string}
 */
export const buildTenantPath = (slug, suffix, matched) => {
  const normalizedSuffix = suffix.startsWith('/') ? suffix : `/${suffix}`;
  if (matched) {
    return normalizedSuffix;
  }
  return `/${slug}${normalizedSuffix}`;
};

/**
 * Combined hook that returns the resolved tenant slug together with a
 * `buildPath` helper bound to the current resolution mode.
 *
 * The `buildPath` function is stable across renders when the slug and matched
 * flag have not changed (wrapped in useCallback).
 *
 * Drop-in replacement for `useTenantPath()` in components that need to
 * construct tenant-aware URLs.
 *
 * @returns {{ tenantSlug: string, buildPath: (suffix: string) => string }}
 */
export const useResolvedTenantPath = () => {
  const { status, tenant } = useHostnameTenant();
  const params = useParams();

  const matched = status === 'matched' && Boolean(tenant?.slug);
  const tenantSlug = matched ? tenant.slug : (params.tenantSlug || '');

  const buildPath = useCallback(
    (suffix = '/') => buildTenantPath(tenantSlug, suffix, matched),
    [tenantSlug, matched]
  );

  return { tenantSlug, buildPath };
};

export default useResolvedTenantSlug;
